// Tools for the app itself: moving between pages, memory, skills
import { tool } from "ai"
import { z } from "zod"
import type { ModuleCatalogEntry, QuestionAnswer, QuestionRequest } from "@/lib/assistant/types"
import type { ModuleRegistry } from "@/lib/assistant/registry"
import * as store from "@/lib/assistant/store"

export interface AppPage {
  title: string
  route: string
  description?: string
  resource?: string
}

// Pages a user can navigate to directly: no :id placeholders, and readable by their role
export function navigablePages(
  pages: Record<string, PageConfig>,
  canRead: (resource: string) => boolean,
): AppPage[] {
  const seen = new Set<string>()
  const out: AppPage[] = []
  for (const p of Object.values(pages)) {
    if (!p.route || p.route.includes(":") || seen.has(p.route)) continue
    if (p.resource && !canRead(p.resource)) continue
    seen.add(p.route)
    out.push({ title: p.title, route: p.route, description: p.description, resource: p.resource })
  }
  return out.sort((a, b) => a.route.localeCompare(b.route))
}

export interface AppModuleContext {
  navigate: (to: string) => void
  getRoute: () => string
  registry: ModuleRegistry
  pages: () => AppPage[]
  catalog: ModuleCatalogEntry[]
  canRead: (resource: string) => boolean
  teamId: () => string | undefined
  canConfigure: () => boolean
  skills: () => store.AssistantSkill[]
  onMemoryChanged: () => void
  // Shows a question card and waits; null when the user skips it
  askQuestion: (q: QuestionRequest) => Promise<QuestionAnswer | null>
}

// Waits until modules stop registering, so the next step sees the new page's tools
async function settle(registry: ModuleRegistry, quietMs = 400, maxMs = 3000) {
  const until = Date.now() + maxMs
  let before = registry.ids().join(",")
  while (Date.now() < until) {
    await registry.waitForChange(quietMs)
    const now = registry.ids().join(",")
    if (now === before) return
    before = now
  }
}

function routeAllowed(route: string, ctx: AppModuleContext): string | null {
  if (!route.startsWith("/")) return "Routes start with /, e.g. /dashboard."
  const path = route.split(/[?#]/)[0]
  if (/^\/agent\/[^/]+$/.test(path)) return ctx.canRead("agents") ? null : "Your role can't open agents."
  if (ctx.pages().some(p => p.route === path)) return null
  return `${path} isn't a page the user can open. Pick one from PAGES THE USER CAN OPEN.`
}

export function makeAppTools(ctx: AppModuleContext) {
  const tools = {
    app_navigate: tool({
      description: "Open a page of the app, like clicking it in the sidebar. Waits for the page to load; its tools are available on your next step.",
      parameters: z.object({
        route: z.string().describe("Path, optionally with a query, e.g. /dashboard, /smart-cms?open=services, /agent/42"),
      }),
      execute: async ({ route }) => {
        const blocked = routeAllowed(route, ctx)
        if (blocked) return { error: blocked }
        ctx.navigate(route)
        await settle(ctx.registry)
        return { ok: true, route: ctx.getRoute(), openModules: ctx.registry.ids() }
      },
    }),

    app_context: tool({
      description: "Where the user is right now and which modules are open.",
      parameters: z.object({}),
      execute: async () => ({
        route: ctx.getRoute(),
        openModules: ctx.registry.list().map(m => ({ id: m.id, title: m.title, context: m.context ?? null })),
      }),
    }),

    memory_save: tool({
      description: "Remember a short fact for future conversations: a preference, a convention, a recurring detail. Not for secrets or beneficiary data.",
      parameters: z.object({
        content: z.string().describe("One or two sentences"),
        shared: z.boolean().nullable().describe("true to share with the whole team (needs permission); null or false for only this user"),
      }),
      execute: async ({ content, shared }) => {
        const teamId = ctx.teamId()
        if (!teamId) return { error: "No team selected." }
        if (shared && !ctx.canConfigure()) return { error: "Your role can't save team-wide notes. Save it for yourself instead." }
        const r = await store.saveMemory(teamId, content.trim(), !!shared)
        if (!r.ok) return { error: r.missing ? "Memory isn't set up yet (the assistant database migration hasn't run)." : r.error }
        ctx.onMemoryChanged()
        return { ok: true, id: r.data }
      },
    }),

    memory_forget: tool({
      description: "Delete a remembered note by its id.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        const r = await store.deleteMemory(id)
        if (!r.ok) return { error: r.error }
        ctx.onMemoryChanged()
        return { ok: true }
      },
    }),

    memory_search: tool({
      description: `Search this user's past assistant conversations (kept for ${store.RETENTION_DAYS} days).`,
      parameters: z.object({ query: z.string().describe("Words to look for") }),
      execute: async ({ query }) => {
        const teamId = ctx.teamId()
        if (!teamId) return { error: "No team selected." }
        const r = await store.searchMessages(teamId, query)
        if (!r.ok) return { error: r.missing ? "Conversation history isn't set up yet." : r.error }
        return { matches: r.data }
      },
    }),

    ask_user: tool({
      description:
        "Ask the user one question with a few answers to pick from, and wait for the answer. Use it when you need them to choose "
        + "(which records, which languages, which of two approaches) rather than asking in your reply. Offer the likely answers "
        + "as options; set allowOther when none may fit. One question at a time.",
      parameters: z.object({
        question: z.string().describe("Short and specific"),
        options: z.array(z.string()).describe("2 to 6 answers, each a few words"),
        multiple: z.boolean().describe("True when several options can be picked together"),
        allowOther: z.boolean().describe("True to also let the user type their own answer"),
      }),
      execute: async ({ question, options, multiple, allowOther }) => {
        const choices = [...new Set(options.map(o => o.trim()).filter(Boolean))].slice(0, 6)
        if (choices.length < 2 && !allowOther) return { error: "Give at least two options, or set allowOther." }
        const answer = await ctx.askQuestion({ question: question.trim(), options: choices, multiple, allowOther })
        if (!answer) return { skipped: true, note: "The user skipped the question. Choose sensibly and say what you chose, or ask in your reply." }
        return answer
      },
    }),

    skill_use: tool({
      description: "Load the full instructions of a team skill by name, then follow them.",
      parameters: z.object({ name: z.string() }),
      execute: async ({ name }) => {
        const s = ctx.skills().find(k => k.enabled && k.name.toLowerCase() === name.replace(/^\//, "").toLowerCase())
        return s ? { name: s.name, instructions: s.instructions } : { error: `No skill named ${name}.` }
      },
    }),
  }

  return tools
}
